import type { HTMLComponent } from "@commercekit/react";
import type { WovarProduct } from "@commercekit/api";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import WvDisclosure from "../atoms/WvDisclosure";
import classNames from "classnames";
import { useId, useMemo } from "react";

const getDescriptionWithScopedStyle = (
  description: WovarProduct["description"],
  skuSelector: string
) => {
  if (typeof description !== "string") {
    return description;
  }

  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/i;
  const match = description.match(styleTagRegex);

  if (!match) {
    return description;
  }

  const styleContent = match[1];

  if (styleContent.includes(skuSelector)) {
    return description;
  }

  const wrappedStyleContent = `${skuSelector} {${styleContent}}`;

  return description.replace(
    styleTagRegex,
    `<style>${wrappedStyleContent}</style>`
  );
};

const InformationTitle: HTMLComponent = () => {
  const { $pt } = usePrismicLabels();
  return (
    <div className="py-2.5">
      <h2 className="font-gotham text-sm lg:text-2xl font-medium mb-0 leading-h lg:mb-4 py-2.5 lg:py-0">
        {$pt("product_label_product_information")}
      </h2>
    </div>
  );
};

const MultiPleColumn: HTMLComponent<{
  description: WovarProduct["description"];
  sku: string;
}> = ({ description, sku }) => {
  const skuSelector = `#sku-${sku}`;

  const descriptionWithScopedStyle = useMemo(() => {
    return getDescriptionWithScopedStyle(description, skuSelector);
  }, [description, skuSelector]);

  return (
    <div
      className="px-0 mx-2.5 lg:px-2.5 lg:mx-0 border-y border-gray-lightest lg:border-y-0 basis-full lg:basis-6/12 mb-0 lg:mb-11.25"
      id="intro"
    >
      <div className="hidden lg:block">
        <InformationTitle />
        <div className="pb-1.25">
          <div
            dangerouslySetInnerHTML={{
              __html: descriptionWithScopedStyle,
            }}
            className="mb-4 text-sm font-normal leading-7 text-gray"
          />
        </div>
      </div>
      <div className="block lg:hidden">
        <WvDisclosure disclosureTitle={<InformationTitle />} defaultOpen={true}>
          <div className="pb-1.25">
            <div
              dangerouslySetInnerHTML={{
                __html: descriptionWithScopedStyle,
              }}
              className="mb-4 text-sm font-normal leading-7 text-gray"
            />
          </div>
        </WvDisclosure>
      </div>
    </div>
  );
};

const FullWidth: HTMLComponent<{
  description: WovarProduct["description"];
  productName: string;
  isSplit: boolean;
  sku: string;
}> = ({ description, productName, isSplit, sku }) => {
  const { $pt } = usePrismicLabels();
  const skuSelector = `#sku-${sku}`;
  const collapseId = useId();

  const descriptionWithScopedStyle = useMemo(() => {
    return getDescriptionWithScopedStyle(description, skuSelector);
  }, [description, skuSelector]);

  return (
    <div className="py-0 lg:pt-16.25">
      <div className="lg:max-w-pc lg:mx-auto">
        <div className="lg:mx-0 pdp-section-card" id="intro">
          <h2 className="py-3 heading md underlined sans-serif strong mdl left">
            {productName}
          </h2>
          <div className="pdp-collapse">
            {/* open by default */}
            <input
              type="checkbox"
              id={collapseId}
              className="pdp-collapse__input"
              defaultChecked
            />
            <div className="pdp-collapse__body pdp-collapse__body--description">
              <div
                dangerouslySetInnerHTML={{
                  __html: descriptionWithScopedStyle,
                }}
                id={`sku-${sku}`}
                className={classNames(
                  "leading-7 font-normal mb-4 lg:mb-0 text-sm text-gray content",
                  {
                    "lg:columns-2": isSplit,
                  }
                )}
              />
              <span className="pdp-collapse__fade" aria-hidden="true" />
            </div>
            <label htmlFor={collapseId} className="pdp-collapse__toggle">
              <span className="pdp-collapse__more">
                {$pt("customer_review_show_more")}
              </span>
              <span className="pdp-collapse__less">
                {$pt("customer_review_show_less")}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  FullWidth,
  MultiPleColumn,
};
